import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import type { ColDef } from 'ag-grid-community'
import { useMemo, useState, useCallback } from 'react'

import type { AccessKey } from '@/api/admin-sales-channels-types'
import { preloadGetSalesChannel, useGetSalesChannel } from '@/api/react-query'
import { AccessKeyDevelopmentModeCell, CreateAccessKeyDrawer } from '@/components/access-key/create-access-key-drawer'
import { Badge } from '@/components/base/badge'
import { Button } from '@/components/base/button'
import { Container, ContainerHeader } from '@/components/base/container'
import { Copy } from '@/components/base/copy'
import { EmptyState } from '@/components/base/empty-state'
import { IconButton } from '@/components/base/icon-button'
import { SectionRow } from '@/components/base/section-row'
import { SingleColumnPage } from '@/components/page-layout'
import { Table } from '@/components/table/table'
import { useI18n } from '@/i18n/i18n-react'
import { PencilSquare } from '@/icons/pencil-square'
import { Plus } from '@/icons/plus'
import { ConditionalRouteComponent } from '@/utils/route-utilities'

import { formatSalesChannelTypeLabel } from './-sales-channel-type-utils'

export const Route = createFileRoute('/_authenticated/settings/sales-channels/$salesChannelUuid')({
	component: () => (
		<ConditionalRouteComponent routePath={Route.id} pathsToRenderOn={['/edit']}>
			<SalesChannelDetail />
		</ConditionalRouteComponent>
	),
	loader: async ({ context, params: { salesChannelUuid } }) => {
		await preloadGetSalesChannel(context, { path: { salesChannelUuid } })
	},
	staticData: {
		crumb: ({ context: { L } }) => L.Settings.SalesChannels.Detail.Title()
	}
})

function SalesChannelDetail() {
	const { L } = useI18n()
	const { salesChannelUuid } = Route.useParams()
	const [createAccessKeyOpen, setCreateAccessKeyOpen] = useState(false)
	const navigate = useNavigate()

	const salesChannel = useGetSalesChannel({ salesChannelUuid })

	const navigateToCreatePOS = useCallback(() => {
		navigate({
			to: '/settings/points-of-sale/create',
			search: { salesChannelUuid }
		})
	}, [navigate, salesChannelUuid])

	const accessKeyColumnDefs = useMemo<ColDef<AccessKey>[]>(
		() => [
			{
				headerName: L.Fields.Name(),
				field: 'name',
				flex: 2
			},
			{
				headerName: L.Settings.SalesChannels.AccessKeyType.Title(),
				field: 'type',
				flex: 1,
				valueGetter: ({ data }) =>
					data?.type === 'SECRET'
						? L.Settings.SalesChannels.AccessKeyType.Api()
						: L.Settings.SalesChannels.AccessKeyType.Publishable()
			},
			{
				headerName: L.Settings.SalesChannels.AccessKeys.Column.Key(),
				flex: 2,
				cellRenderer: ({ data }: { data: AccessKey }) => {
					if (!data) return null
					const isSecret = data.type === 'SECRET'
					const display = isSecret ? data.redacted : `${data.token.substring(0, 20)}...`
					return (
						<div className='flex h-full items-center gap-2'>
							<code className='rounded bg-ui-bg-subtle px-2 py-1 text-xs'>{display}</code>
							{!isSecret && <Copy content={data.token} variant='mini' />}
						</div>
					)
				}
			},
			{
				headerName: L.Fields.CreatedAt(),
				field: 'createdAt',
				type: 'date',
				flex: 1
			},
			{
				headerName: L.Settings.AccessKeys.Field.DevelopmentMode(),
				flex: 1,
				cellRenderer: AccessKeyDevelopmentModeCell
			}
		],
		[L]
	)

	const pointOfSaleColumnDefs = useMemo<ColDef[]>(
		() => [
			{
				headerName: L.Fields.Name(),
				field: 'name',
				flex: 2
			},
			{
				headerName: L.Settings.PointOfSale.Column.Identifier(),
				field: 'identifier',
				flex: 1
			}
		],
		[L]
	)

	return (
		<SingleColumnPage hasOutlet>
			{/* Sales Channel Info */}
			<Container className='divide-y'>
				<ContainerHeader
					heading={salesChannel.data?.name || L.Settings.SalesChannels.Detail.Title()}
					rightSide={
						<Link from={Route.fullPath} to='./edit'>
							<IconButton tooltipText={L.General.Edit()}>
								<PencilSquare />
							</IconButton>
						</Link>
					}
				/>
				<SectionRow
					title={L.Settings.SalesChannels.Column.Group()}
					value={
						salesChannel.data?.group ? (
							<Link
								to='/settings/sales-channels/groups/$groupUuid'
								params={{ groupUuid: salesChannel.data.group.uuid }}
							>
								<Badge size='2xsmall'>{salesChannel.data.group.name}</Badge>
							</Link>
						) : (
							L.Settings.SalesChannels.GroupNone()
						)
					}
				/>
				{salesChannel.data?.countryCode && (
					<SectionRow
						title={L.Settings.SalesChannels.Column.CountryCode()}
						value={salesChannel.data.countryCode}
					/>
				)}
				{salesChannel.data?.currencyCode && (
					<SectionRow
						title={L.Settings.SalesChannels.Column.CurrencyCode()}
						value={salesChannel.data.currencyCode}
					/>
				)}
				<SectionRow
					title={L.Settings.SalesChannels.Column.SalesChannelType()}
					value={formatSalesChannelTypeLabel(salesChannel.data?.salesChannelType, L)}
				/>
				<SectionRow
					title={L.Settings.SalesChannels.Column.InventoryLocation()}
					value={
						salesChannel.data?.inventoryLocation?.name ?? L.Settings.SalesChannels.InventoryLocationNone()
					}
				/>
			</Container>

			{/* API Keys */}
			<Container>
				<ContainerHeader
					heading={L.Settings.SalesChannels.Detail.AccessKeys()}
					rightSide={
						<div className='flex items-center gap-x-2'>
							<Link to='/settings/access-keys'>
								<Button variant='secondary' size='small'>
									{L.Settings.AccessKeys.Manage()}
								</Button>
							</Link>
							<IconButton tooltipText={L.General.Create()} onClick={() => setCreateAccessKeyOpen(true)}>
								<Plus />
							</IconButton>
						</div>
					}
				/>
				<Table
					className='w-full'
					data={salesChannel.data?.accessKeys}
					columnUtilities={{}}
					columnDefs={accessKeyColumnDefs}
					animateRows
					domLayout='autoHeight'
					rowSelection={{
						mode: 'singleRow',
						checkboxes: false,
						enableClickSelection: false,
						enableSelectionWithoutKeys: false
					}}
					suppressCellFocus
					emptyState={
						<EmptyState
							title='No access keys'
							description='Create access keys to allow external systems to access this sales channel.'
							className='min-h-[200px]'
							action={{
								label: L.General.Create(),
								onClick: () => setCreateAccessKeyOpen(true)
							}}
						/>
					}
				/>
			</Container>

			{/* Point of Sales */}
			<Container>
				<ContainerHeader
					heading={L.Settings.SalesChannels.Detail.PointOfSales()}
					rightSide={
						<IconButton tooltipText={L.General.Create()} onClick={navigateToCreatePOS}>
							<Plus />
						</IconButton>
					}
				/>
				<Table
					className='w-full'
					data={salesChannel.data?.pointOfSales}
					columnUtilities={{}}
					columnDefs={pointOfSaleColumnDefs}
					animateRows
					domLayout='autoHeight'
					rowSelection={{
						mode: 'singleRow',
						checkboxes: false,
						enableClickSelection: false,
						enableSelectionWithoutKeys: false
					}}
					suppressCellFocus
					rowToRouterConfig={pos => ({
						to: '/settings/points-of-sale/$pointOfSaleUuid',
						params: { pointOfSaleUuid: pos.uuid }
					})}
					emptyState={
						<EmptyState
							title='No points of sale configured'
							description='Create a point of sale to start accepting payments.'
							className='min-h-[200px]'
							action={{
								label: L.General.Create(),
								onClick: navigateToCreatePOS
							}}
						/>
					}
				/>
			</Container>

			<CreateAccessKeyDrawer
				open={createAccessKeyOpen}
				onOpenChange={setCreateAccessKeyOpen}
				salesChannelUuids={[salesChannelUuid]}
			/>
		</SingleColumnPage>
	)
}
